import React from 'react';
import moment from 'moment-timezone';

import CoachingConstants, { CoachingSessionDuration } from 'coaching/CoachingConstants';
import Grid from 'components/Grid';
import Heading from 'components/Heading';
import Row from 'components/Row';
import SelectBox from 'components/SelectBox';
import TimezoneHelper from 'lib/timezones';
import { dayHasNoSlots } from 'coaching/components/CoachingHomeView/BookRecurringSession';
import {
  RecurringCoachingSessionSlotGroupings,
  SlotsForDay,
} from 'coaching/lib/coachingSessionSlots';
import { CoachingSessionSlot } from 'coaching/containers/GetPossibleCoachingSessionsForCoachContainer';
import { DayOfWeek } from 'globalTypes';

export function DaySelectRow({
  groupedSlots,
  onSelectDay,
  selectedDay,
}: {
  groupedSlots: RecurringCoachingSessionSlotGroupings;
  onSelectDay: (day: DayOfWeek) => void;
  selectedDay?: DayOfWeek;
}) {
  return (
    <SelectBoxGridRow columns={CoachingConstants.VALID_COACHING_DAYS.length} label="Day">
      {CoachingConstants.VALID_COACHING_DAYS.map(day => (
        <SelectBox
          key={day}
          data-test={`day-select-box-${day.toString()}`}
          flush
          full
          disabled={dayHasNoSlots(day, groupedSlots)}
          onSelect={() => onSelectDay(day)}
          selected={selectedDay === day}
          label={moment(day, 'dddd').format('ddd')}
        />
      ))}
    </SelectBoxGridRow>
  );
}

export function TimeSelectRow({
  slotsForSelectedDay,
  onSelectTime,
  selectedTime,
}: {
  slotsForSelectedDay: SlotsForDay;
  onSelectTime: (time: string) => void;
  selectedTime?: string;
}) {
  return (
    <SelectBoxGridRow columns={2} label={`Time (${TimezoneHelper.userTimezoneReadableShortcode})`}>
      {Object.keys(slotsForSelectedDay).map(time => (
        <SelectBox
          key={time}
          data-test={`time-select-box-${time}`}
          flush
          full
          onSelect={() => onSelectTime(time)}
          selected={selectedTime === time}
          label={time}
        />
      ))}
    </SelectBoxGridRow>
  );
}

export function SlotSelectRow({
  slotsForSelectedTime,
  onSelectSlot,
  selectedSlot,
}: {
  slotsForSelectedTime?: CoachingSessionSlot[];
  onSelectSlot: (slot: CoachingSessionSlot) => void;
  selectedSlot?: CoachingSessionSlot;
}) {
  return (
    <SelectBoxGridRow columns={2} label="Start date">
      {(slotsForSelectedTime ?? []).map(slot => (
        <SelectBox
          key={slot.startDateTime.toISOString()}
          data-test={`slot-select-box-${slot.startDateTime.toISOString()}`}
          flush
          full
          onSelect={() => onSelectSlot(slot)}
          selected={slot.startDateTime.isSame(selectedSlot?.startDateTime)}
          label={slot.startDateTime.format('ddd, MMM Do')}
        />
      ))}
    </SelectBoxGridRow>
  );
}

export function DurationSelectRow({
  onSelectDuration,
  selectedDuration,
}: {
  onSelectDuration: (duration: CoachingSessionDuration) => void;
  selectedDuration?: CoachingSessionDuration;
}) {
  return (
    <SelectBoxGridRow columns={2} label="Length">
      {CoachingConstants.COACHING_SESSION_DURATIONS.map(duration => (
        <SelectBox
          key={duration}
          data-test={`duration-select-box-${duration}`}
          flush
          full
          onSelect={() => onSelectDuration(duration)}
          selected={selectedDuration === duration}
          label={`${duration} minutes`}
          textSize="xsmall"
        />
      ))}
    </SelectBoxGridRow>
  );
}

export function SelectBoxGridRow({
  columns,
  label,
  children,
}: {
  columns: number;
  label: string;
  children: React.ReactElement<typeof SelectBox>[];
}) {
  return (
    <Row>
      <Row size="xsmall">
        <Heading level="5" flush>
          {label}
        </Heading>
      </Row>
      <Grid columns={columns} gap="xxsmall">
        {children}
      </Grid>
    </Row>
  );
}
